import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from 'react';
import {Intent, MenuItem} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {ItemRenderer, MultiSelect2} from '@blueprintjs/select';

import {IUser} from '../../models';
import {Callback, Recipient} from './types';
import {
  createNewItemFromPaste,
  createNewItemFromQuery,
  getInitialValue,
  onFilerRecipients,
  recipientsToOptions,
} from './utils';

import './SelectRecipients.scss';

interface SelectRecipientsProps {
  defaultValue: string[]; // array of emails
  disabled: boolean;
  recipients: Partial<IUser>[];
  onChange: Callback;
  placeholder?: string;
}

const RecipientsMultiSelect = MultiSelect2.ofType<Recipient>();

const renderItem: ItemRenderer<Recipient> = (
  item: Recipient,
  { handleClick, modifiers, query }: any
) => {
  if (!modifiers.matchesPredicate) {
      return null;
  }

  return (
    <MenuItem
      text={item.email}
      key={item.uid || item.email}
      onClick={handleClick} />
  )
}

const createNewItemRenderer = (
  query: string,
  active: boolean,
  handleClick: React.MouseEventHandler<HTMLElement>,
) => (
  <MenuItem
    icon={IconNames.ADD}
    text={`Add "${query}"`}
    active={active}
    onClick={handleClick}
    shouldDismissPopover={false}
  />
);

const tagRenderer = (item: Recipient) => item.email;

const SelectRecipients = forwardRef(({
  defaultValue,
  disabled,
  recipients,
  onChange,
  placeholder = 'Select em\uFEFFail add\uFEFFress', // \uFEFF - used to disable autocomplete
}: SelectRecipientsProps, ref: any) => {
  const options = useMemo(() => recipientsToOptions(recipients), [recipients.length]);
  const initialValue = getInitialValue(defaultValue, options)
  const [selectedItems, setSelectedItems] = useState<Recipient[]>(initialValue);
  const [filteredItems, setFilteredItems] = useState<Recipient[]>([]); // items that can be selected
  const [error, setError] = useState<string>('');
  const [init, setInit] = useState<boolean>(false); // avoid re-adding default values onClear

  useEffect(() => {
    if (options.length) {
      setFilteredItems(options);
    }
  }, [options?.length]);

  useEffect(() => {
    /*
     * Remove selected items from the list of available items;
     * Trigger onChange callback;
     */
    const selected: string[] = selectedItems.map((selectedItem: Recipient) => selectedItem.email as string);
    setFilteredItems(options.filter((item: any) => !selected.includes(item.email as string)));

    onChange_(selected);
  }, [selectedItems?.length]);

  useEffect(() => {
    /*
     * If defaultValue is provided, select items from the list of available items;
     */
    if (init) {
      return;
    }
    setInit(true);

    if (defaultValue?.length && !selectedItems.length) {
      const selected: Recipient[] = [];

      defaultValue.forEach((email: string) => {
        const found = options.find((item: Recipient) => item.email === email);

        if (found) {
          selected.push(found);
        } else {
          selected.push(createNewItemFromQuery(email));
        }
      });

      if (selected.length) {
        setSelectedItems(selected);
      }
    }
  }, [defaultValue, selectedItems, init]);

  useImperativeHandle(ref, () => ({
    validate() {
      const isValid = selectedItems.length > 0;

      if (!isValid) {
        setError('No recipients selected.');
      }
      return isValid;
    },
    getValue() {
      return selectedItems.map((item: Recipient) => item.email);
    }
  }));

  const onItemSelect = (item: Recipient) => {
    setSelectedItems((oldItems: Recipient[]) => [...oldItems, item]);
  };

  const onItemRemove = (item: Recipient) => {
    setSelectedItems(
      (oldItems: Recipient[]) =>
        [...oldItems.filter((oldItem: Recipient) => oldItem.email !== item.email)]
    );
  };

  const onItemsPaste = (items: Recipient[]) => {
    setSelectedItems((oldItems: Recipient[]) => [
      ...oldItems,
      ...createNewItemFromPaste(items)
    ]);
  }

  const onClear = () => {
    setSelectedItems([]);
  };

  const onChange_ = (value: any) => {
    if (init) {
      onChange(value);
    }
  }

  return (
    <>
      <RecipientsMultiSelect
        fill={true}
        disabled={disabled}
        placeholder={placeholder}
        items={filteredItems}
        itemPredicate={onFilerRecipients}
        selectedItems={selectedItems}
        itemRenderer={renderItem}
        onItemSelect={onItemSelect}
        onRemove={onItemRemove}
        resetOnSelect={true}
        tagRenderer={tagRenderer}
        createNewItemFromQuery={createNewItemFromQuery}
        createNewItemRenderer={createNewItemRenderer}
        onItemsPaste={onItemsPaste}
        popoverProps={{
          minimal: true,
          captureDismiss: true /* close only this popover */,
          portalClassName: 'select-recipients-portal',
          usePortal: true,
        }}
        tagInputProps={{
          intent: error ? Intent.DANGER : undefined,
        }}
        onClear={selectedItems?.length > 0 && ! disabled ? onClear : undefined}
      />
      <div className="input-error">{error}</div>
    </>
  );
});

export default SelectRecipients;
